import {
  IScreeningItemAvailableChildrenProperties,
  IScreeningItemAvailableValues,
} from '@/interfaces/portfolioOptimization.interface'
import { Box, Button, Typography } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import React, { useEffect, useMemo, useState } from 'react'

type TreeViewListProps = {
  items: IScreeningItemAvailableValues[]
  selectedItems?: string[]
  onSelectedItems: (
    treeItems: IScreeningItemAvailableChildrenProperties[],
  ) => void
  searchTerm?: string
  title: string
}

export const TreeViewList: React.FC<TreeViewListProps> = ({
  items = [],
  onSelectedItems,
  selectedItems = [],
  searchTerm = '',
  title = 'Select Benchmark',
}: TreeViewListProps) => {
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(selectedItems)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  let count = 0

  // Filter items and determine expanded nodes
  const filterItems = (
    items: IScreeningItemAvailableValues[],
    query: string,
  ): IScreeningItemAvailableValues[] => {
    return items
      .map((item) => {
        const matches = item.title?.toLowerCase().includes(query.toLowerCase())
        const filteredChildren = item.children
          ? filterItems(item.children, query)
          : []

        if (matches || filteredChildren.length > 0) {
          const itemId = item.id || count++
          return {
            ...item,
            id: itemId,
            label: item.title,
            children: filteredChildren,
            originalItem: {
              title: item.title,
              name: item.name,
              value: item.value,
              code: item.code,
              id: item.id,
            },
          }
        }
        return null
      })
      .filter((item) => item !== null) as IScreeningItemAvailableValues[]
  }

  const filteredItems = useMemo(
    () => filterItems(items, searchTerm),
    [items, searchTerm],
  )

  const getOriginalObjectsFromIds = (
    nodes: IScreeningItemAvailableValues[],
    ids: string[],
  ): IScreeningItemAvailableChildrenProperties[] => {
    const result: IScreeningItemAvailableChildrenProperties[] = []
    const idSet = new Set(ids)

    const traverse = (items: IScreeningItemAvailableValues[]) => {
      for (const item of items) {
        if (idSet.has(item.id)) {
          result.push(
            item.originalItem as IScreeningItemAvailableChildrenProperties,
          )
          if (result.length === ids.length) {
            return true
          }
        }
        if (item.children?.length) {
          const done = traverse(item.children)
          if (done) return true
        }
      }

      return false
    }

    traverse(nodes)
    return result
  }

  const collectDescendantIds = (
    nodes: IScreeningItemAvailableValues[],
    targetId: string,
  ): string[] => {
    const result: string[] = []

    const traverse = (items: IScreeningItemAvailableValues[]): boolean => {
      for (const item of items) {
        if (item.id === targetId) {
          const collectAll = (node: IScreeningItemAvailableValues) => {
            result.push(node.id as string)
            if (node.children?.length) {
              node.children.forEach((child) => collectAll(child))
            }
          }
          collectAll(item)
          return true
        }
        if (item.children?.length) {
          const found = traverse(item.children)
          if (found) return true
        }
      }
      return false
    }

    traverse(nodes)
    return result
  }

  useEffect(() => {
    if (searchTerm) {
      // Expand all nodes that contain search results
      const collectNodeIds = (
        nodes: IScreeningItemAvailableValues[],
      ): string[] => {
        let ids: string[] = []
        nodes.forEach((node: any) => {
          ids.push(node.id)
          if (node.children) {
            ids = ids.concat(collectNodeIds(node.children))
          }
        })
        return ids
      }

      setExpandedNodes(new Set(collectNodeIds(filteredItems)))
      setIsExpanded(true)
    } else {
      // Collapse all nodes when there is no search term
      setExpandedNodes(new Set())
      setIsExpanded(false)
    }
  }, [searchTerm, filteredItems])

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent,
    ids: string[],
  ) => {
    const previousIdsSet = new Set<string>(selectedOptions)
    const currentIdsSet = new Set<string>(ids)

    const addedIds = ids.filter((id) => !previousIdsSet.has(id))
    const removedIds = selectedOptions.filter((id) => !currentIdsSet.has(id))

    const finalIdsSet = new Set<string>(ids)

    addedIds.forEach((id) => {
      const descendants = collectDescendantIds(filteredItems, id)
      descendants.forEach((descId) => finalIdsSet.add(descId))
    })

    removedIds.forEach((id) => {
      const descendants = collectDescendantIds(filteredItems, id)
      descendants.forEach((descId) => finalIdsSet.delete(descId))
    })

    const finalIds = Array.from(finalIdsSet)
    const selectedItemsRaw = getOriginalObjectsFromIds(filteredItems, finalIds)
    const selectedItems = selectedItemsRaw.filter(
      (item) => item && (item as any).name,
    ) as IScreeningItemAvailableChildrenProperties[]

    setSelectedOptions(finalIds)
    onSelectedItems(selectedItems)
  }

  const handleToggleExpandCollapse = () => {
    if (isExpanded) {
      // Collapse all
      setExpandedNodes(new Set())
    } else {
      // Expand all
      const collectNodeIds = (
        nodes: IScreeningItemAvailableValues[],
      ): string[] => {
        let ids: string[] = []
        nodes.forEach((node) => {
          ids.push(node.id)
          if (node.children) {
            ids = ids.concat(collectNodeIds(node.children))
          }
        })
        return ids
      }

      setExpandedNodes(new Set(collectNodeIds(items)))
    }
    setIsExpanded(!isExpanded)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: '#E5E5E5',
        }}
      >
        <Typography variant='h5' sx={{ padding: 1 }}>
          {title}
        </Typography>
        <Button
          sx={{ marginRight: 1 }}
          size='small'
          onClick={handleToggleExpandCollapse}
        >
          {isExpanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </Box>
      <RichTreeView
        sx={{
          height: '100%',
          flexGrow: 1,
          overflowY: 'auto',
          border: '1px solid #E5E5E5',
        }}
        checkboxSelection={true}
        multiSelect
        items={filteredItems}
        selectedItems={selectedOptions}
        expandedItems={Array.from(expandedNodes)}
        onSelectedItemsChange={handleSelectedItemsChange}
        onExpandedItemsChange={(event, ids) => setExpandedNodes(new Set(ids))}
      />
    </Box>
  )
}
